import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  fetchCoursesList,
  fetchCourseDetail,
  fetchTracksList,
  fetchTrackDetail,
  fetchCoursePresences,
  fetchAssignedCourses,
  fetchAssignedTracks,
  fetchInstructorStudents,
  sendInstructorStudentMessage,
  InstructorStudentMessagePayload,
  createCourse,
  updateCourse,
  deleteCourse,
  createTrack,
  updateTrack,
  deleteTrack,
  CourseData,
  TrackData,
} from "../../services/curriculumService";

/**
 * Query hook for instructor assigned courses and tracks
 */
export const useInstructorCurriculumQuery = () => {
  return useQuery({
    queryKey: [...queryKeys.curriculumAdmin.all, "assigned"],
    queryFn: async () => {
      const [courses, tracks] = await Promise.all([
        fetchAssignedCourses(),
        fetchAssignedTracks(),
      ]);
      return {
        courses: courses || [],
        tracks: tracks || [],
      };
    },
  });
};

/**
 * Query hook for instructor students directory, enrollments, progress & KPIs
 */
export const useInstructorStudentsQuery = (scope?: string) => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.instructorStudents(scope),
    queryFn: () => fetchInstructorStudents(scope),
  });
};

/**
 * Mutation hook to send a direct or broadcast notification message to enrolled students
 */
export const useSendInstructorMessageMutation = () => {
  return useMutation({
    mutationFn: (payload: InstructorStudentMessagePayload) =>
      sendInstructorStudentMessage(payload),
  });
};

/**
 * Query hook for administrative course listing (Instructors & SuperAdmin)
 */
export const useAdminCoursesQuery = () => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.coursesList(),
    queryFn: () => fetchCoursesList(),
  });
};

/**
 * Query hook for administrative career track listing
 */
export const useAdminTracksQuery = () => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.tracksList(),
    queryFn: () => fetchTracksList(),
  });
};

/**
 * Query hook for admin course detail
 */
export const useAdminCourseDetailQuery = (idOrSlug?: string | number) => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.courseDetail(idOrSlug || ""),
    queryFn: () => fetchCourseDetail(idOrSlug!),
    enabled: Boolean(idOrSlug),
  });
};

/**
 * Query hook for admin track detail
 */
export const useAdminTrackDetailQuery = (idOrSlug?: string | number) => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.trackDetail(idOrSlug || ""),
    queryFn: () => fetchTrackDetail(idOrSlug!),
    enabled: Boolean(idOrSlug),
  });
};

/**
 * Query hook for course delivery presences (Online, Onsite, Hybrid)
 */
export const usePresencesQuery = () => {
  return useQuery({
    queryKey: queryKeys.curriculumAdmin.presences(),
    queryFn: () => fetchCoursePresences(),
  });
};

/**
 * Mutation hook to create a new course
 */
export const useCreateCourseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CourseData>) => createCourse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
    },
  });
};

/**
 * Mutation hook to update a course
 */
export const useUpdateCourseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<CourseData> }) =>
      updateCourse(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.courseDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
    },
  });
};

/**
 * Mutation hook to delete a course
 */
export const useDeleteCourseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
    },
  });
};

/**
 * Mutation hook to update a track
 */
export const useUpdateTrackMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<TrackData> }) =>
      updateTrack(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.trackDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tracks.all });
    },
  });
};

/**
 * Mutation hook to delete a track
 */
export const useDeleteTrackMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => deleteTrack(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.curriculumAdmin.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tracks.all });
    },
  });
};
